// Interactive 3D view for the Load Truck Simulator.
//
// Differs from the read-only TruckLoad3D (used by the trips page) in three
// ways:
//   - Items are driven by an external `placements` state (not by packIntoTruck),
//     so the user can reposition them freely.
//   - Drag-to-move: pointerdown on an item → pointermove raycasts onto an
//     invisible floor plane → placement state updates on every frame →
//     pointerup releases. OrbitControls is disabled during a drag.
//   - Click to select: clicking an item (without dragging) selects it and
//     pops an HTML overlay with Rotate/Remove buttons via drei's <Html>.
//
// Truck-local coordinate convention:
//   x ∈ [0, truck.lengthM]  → inside truck, 0 at back, lengthM at cab
//   z ∈ [0, truck.widthM]   → inside truck
//   z < 0                   → staging area (left of truck, for unloaded items)
// World coords in the Canvas are just these shifted so the truck centers
// at world origin (same convention as TruckLoad3D).

import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Text, ContactShadows, Html } from '@react-three/drei';

import {
  itemFootprint,
  nextOrientation,
  findPlacementCollisions,
  placementFitsTruck,
  placementOnTruckBed,
  drumSpecLabel,
} from '../../features/warehouse/warehouseUtils.js';

const COLLISION_COLOR = '#dc3545';

const TYPE_HEX = {
  drum: '#b8860b',
  europallet: '#3a95c9',
};

// Staging area sits on the left (-Z side) of the truck. Width is scaled
// with the truck length so long loads still have room to spread out.
const STAGING_WIDTH_M = 4;
const STAGING_GAP_M = 0.4;

export default function SimTruckLoad3D({
  truck,
  placements,
  onMove,        // (id, { x, z }) — called during drag
  onRotate,      // (id, nextOrientation)
  onRemove,      // (id)
  onSelect,      // (id | null)
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [isDragging, setDragging] = useState(false);

  // Pre-compute which ids are in collision and which spill past the truck
  // walls. Both conditions colour the item red in the 3D view.
  const collidingIds = useMemo(
    () => findPlacementCollisions(placements),
    [placements]
  );
  const outOfBoundsIds = useMemo(() => {
    const s = new Set();
    for (const p of placements) {
      // Only enforce truck-wall fit for items the user put ON the bed;
      // staged items can spill past the staging patch without flagging.
      if (placementOnTruckBed(p, truck) && !placementFitsTruck(p, truck)) {
        s.add(p.id);
      }
    }
    return s;
  }, [placements, truck]);

  // Shift so truck centre is at world origin.
  const offsetX = -truck.lengthM / 2;
  const offsetZ = -truck.widthM / 2;

  // Camera framed so both truck + staging area fit in view.
  const totalWidth = truck.widthM + STAGING_GAP_M + STAGING_WIDTH_M;
  const cameraPosition = useMemo(
    () => [truck.lengthM * 0.75, truck.heightM * 3.2, totalWidth * 1.8],
    [truck, totalWidth]
  );

  const handleSelect = useCallback(id => {
    setSelectedId(id);
    onSelect?.(id);
  }, [onSelect]);

  return (
    <div style={{ height: 620, background: '#eef2f6', borderRadius: 6, position: 'relative' }}>
      <Canvas camera={{ position: cameraPosition, fov: 42 }} shadows dpr={[1, 2]}>
        <color attach="background" args={['#eef2f6']} />
        <fog attach="fog" args={['#eef2f6', truck.lengthM * 1.8, truck.lengthM * 4]} />

        <ambientLight intensity={0.55} />
        <directionalLight
          position={[truck.lengthM * 0.6, truck.heightM * 5, truck.widthM * 2.5]}
          intensity={1.1}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-left={-truck.lengthM}
          shadow-camera-right={truck.lengthM}
          shadow-camera-top={truck.lengthM}
          shadow-camera-bottom={-truck.lengthM}
        />

        <OrbitControls
          target={[0, truck.heightM / 2, 0]}
          enablePan
          enableRotate={!isDragging}
          enableZoom={!isDragging}
          maxPolarAngle={Math.PI / 2.05}
          minDistance={truck.widthM * 1.5}
          maxDistance={truck.lengthM * 3.5}
        />

        <TruckShell truck={truck} />
        <StagingArea truck={truck} offsetX={offsetX} />

        {/* Invisible drag plane — captures pointer moves while dragging */}
        <DragPlane
          truck={truck}
          offsetX={offsetX}
          offsetZ={offsetZ}
          isDragging={isDragging}
          onEnd={() => setDragging(false)}
          onMove={(worldX, worldZ) => {
            if (!selectedId) return;
            const localX = worldX - offsetX;
            const localZ = worldZ - offsetZ;
            onMove?.(selectedId, { x: localX, z: localZ });
          }}
          // Click empty → deselect
          onEmptyClick={() => handleSelect(null)}
        />

        {placements.map(p => (
          <PackedItem
            key={p.id}
            placement={p}
            offsetX={offsetX}
            offsetZ={offsetZ}
            isSelected={p.id === selectedId}
            isInvalid={collidingIds.has(p.id) || outOfBoundsIds.has(p.id)}
            onDragStart={() => { handleSelect(p.id); setDragging(true); }}
            onClick={() => handleSelect(p.id)}
            onRotate={() => onRotate?.(p.id, nextOrientation(p.item, p.orientation))}
            onRemove={() => { onRemove?.(p.id); handleSelect(null); }}
          />
        ))}

        <ContactShadows
          position={[0, -0.05, 0]}
          opacity={0.5}
          scale={truck.lengthM * 2.2}
          blur={2}
          far={truck.heightM * 2}
        />
      </Canvas>

      <div style={{
        position: 'absolute', left: 12, bottom: 12,
        background: 'rgba(33, 37, 41, 0.85)', color: 'white',
        padding: '6px 10px', borderRadius: 4, fontSize: '0.72rem', pointerEvents: 'none',
      }}>
        <div><strong>Click</strong> item to select · <strong>Drag</strong> to move · buttons on the item to rotate/remove</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Truck shell (same walls as TruckLoad3D, copy to keep the components independent)
// ---------------------------------------------------------------------------

function TruckShell({ truck }) {
  const { lengthM, widthM, heightM } = truck;
  const wallColor = '#cfd4da';
  const wallOpacity = 0.22;
  return (
    <group>
      <mesh position={[0, -0.025, 0]} receiveShadow>
        <boxGeometry args={[lengthM, 0.05, widthM]} />
        <meshStandardMaterial color="#6f7479" />
      </mesh>
      <mesh position={[lengthM / 2, heightM / 2, 0]}>
        <boxGeometry args={[0.05, heightM, widthM]} />
        <meshStandardMaterial color={wallColor} transparent opacity={wallOpacity} />
      </mesh>
      <mesh position={[0, heightM / 2, -widthM / 2]}>
        <boxGeometry args={[lengthM, heightM, 0.05]} />
        <meshStandardMaterial color={wallColor} transparent opacity={wallOpacity} />
      </mesh>
      <mesh position={[0, heightM / 2, widthM / 2]}>
        <boxGeometry args={[lengthM, heightM, 0.05]} />
        <meshStandardMaterial color={wallColor} transparent opacity={wallOpacity} />
      </mesh>
      <Text
        position={[0, heightM + 0.3, 0]}
        fontSize={0.28}
        color="#495057"
        anchorX="center"
        anchorY="middle"
      >
        {`${lengthM} × ${widthM} × ${heightM} m`}
      </Text>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Staging area (visible floor patch to the left of the truck, -Z side)
// ---------------------------------------------------------------------------

function StagingArea({ truck }) {
  // Centre of staging patch, world coords. Truck is centered at Z=0 with
  // width truck.widthM, so left wall is at world Z = -truck.widthM/2 and the
  // staging patch sits at world Z = -truck.widthM/2 - STAGING_GAP_M - STAGING_WIDTH_M/2.
  const centerZ = -truck.widthM / 2 - STAGING_GAP_M - STAGING_WIDTH_M / 2;
  return (
    <group>
      <mesh position={[0, -0.03, centerZ]} receiveShadow>
        <boxGeometry args={[truck.lengthM, 0.02, STAGING_WIDTH_M]} />
        <meshStandardMaterial color="#d4d9de" />
      </mesh>
      {/* Dashed outline so the staging zone is visually distinct */}
      <Text
        position={[0, 0.02, centerZ]}
        rotation={[-Math.PI / 2, 0, 0]}
        fontSize={0.35}
        color="#8a929a"
        anchorX="center"
        anchorY="middle"
      >
        STAGING AREA
      </Text>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Drag plane — invisible, handles pointermove while something is dragging
// ---------------------------------------------------------------------------

function DragPlane({ truck, offsetX, offsetZ, isDragging, onMove, onEnd, onEmptyClick }) {
  // Cover both truck bed and staging area with a generous margin.
  const halfLen = truck.lengthM / 2 + 1;
  const span = truck.widthM + STAGING_GAP_M + STAGING_WIDTH_M + 1;
  const centerZ = -(STAGING_GAP_M + STAGING_WIDTH_M) / 2;

  return (
    <mesh
      position={[0, 0, centerZ]}
      rotation={[-Math.PI / 2, 0, 0]}
      onPointerMove={e => {
        if (!isDragging) return;
        e.stopPropagation();
        onMove(e.point.x, e.point.z);
      }}
      onPointerUp={e => {
        if (!isDragging) return;
        e.stopPropagation();
        onEnd();
      }}
      onPointerLeave={() => {
        if (isDragging) onEnd();
      }}
      onClick={() => onEmptyClick?.()}
    >
      <planeGeometry args={[halfLen * 2, span]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// One item (drum or europallet) — positioned from placement.x/z, supports
// drag-to-move and click-to-select with HTML overlay.
// ---------------------------------------------------------------------------

function PackedItem({
  placement, offsetX, offsetZ,
  isSelected, isInvalid, onDragStart, onClick,
  onRotate, onRemove,
}) {
  // To distinguish "drag" from "click": track whether the pointer moved
  // significantly between down and up. A plain click selects; a drag moves.
  // Keep this hook BEFORE any early return so its position in the hook list
  // stays stable across renders (rules-of-hooks).
  const downInfoRef = useRef(null);

  const { item, x, z, y, orientation } = placement;
  const footprint = itemFootprint(item, orientation);
  if (!footprint) return null;

  const color = TYPE_HEX[item.type] || '#adb5bd';
  const worldX = offsetX + x;
  const worldZ = offsetZ + z;
  const restY = (y || 0) + footprint.heightM / 2;
  const handlePointerDown = e => {
    e.stopPropagation();
    downInfoRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
  };
  const handlePointerUp = e => {
    const info = downInfoRef.current;
    downInfoRef.current = null;
    if (!info) return;
    const dx = Math.abs(e.clientX - info.x);
    const dy = Math.abs(e.clientY - info.y);
    // < 5px move treated as click
    if (dx < 5 && dy < 5) {
      e.stopPropagation();
      onClick();
    }
  };
  const handlePointerMove = e => {
    const info = downInfoRef.current;
    if (!info) return;
    const dx = Math.abs(e.clientX - info.x);
    const dy = Math.abs(e.clientY - info.y);
    if (dx > 5 || dy > 5) {
      // Upgrade to drag.
      downInfoRef.current = null;
      e.stopPropagation();
      onDragStart();
    }
  };

  // Red wins over selected-yellow so the user always sees invalid placements.
  const hiColor = isInvalid
    ? COLLISION_COLOR
    : isSelected
      ? '#fcd34d'
      : color;

  const body = footprint.shape === 'cylinder'
    ? <DrumMesh footprint={footprint} color={hiColor} />
    : <BoxMesh footprint={footprint} color={hiColor} item={item} />;

  return (
    <group
      position={[worldX, restY, worldZ]}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerMove={handlePointerMove}
    >
      {body}

      {isSelected && (
        <Html
          position={[0, footprint.heightM / 2 + 0.4, 0]}
          center
          distanceFactor={8}
          style={{ pointerEvents: 'auto' }}
        >
          <div style={{
            background: 'rgba(33, 37, 41, 0.92)',
            color: 'white',
            padding: '4px 6px',
            borderRadius: 4,
            fontSize: 11,
            whiteSpace: 'nowrap',
            display: 'flex',
            gap: 4,
            alignItems: 'center',
          }}>
            <span style={{ marginRight: 6 }}>
              <strong>{item.serial}</strong>
              {drumSpecLabel(item) && (
                <span style={{ opacity: 0.7, marginLeft: 6, textTransform: 'capitalize' }}>
                  {drumSpecLabel(item)}
                </span>
              )}
            </span>
            <button
              type="button"
              onPointerDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); onRotate(); }}
              style={btnStyle('#3a95c9')}
              title="Rotate"
            >↻</button>
            <button
              type="button"
              onPointerDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); onRemove(); }}
              style={btnStyle('#dc3545')}
              title="Remove"
            >✕</button>
          </div>
        </Html>
      )}
    </group>
  );
}

function btnStyle(bg) {
  return {
    background: bg, color: 'white', border: 'none',
    borderRadius: 3, padding: '2px 6px',
    cursor: 'pointer', fontSize: 12, lineHeight: 1,
  };
}

// ---------------------------------------------------------------------------
// Shape components (drum + box), respect orientation via a rotated inner group
// ---------------------------------------------------------------------------

function DrumMesh({ footprint, color }) {
  const radius = footprint.diameterM / 2;
  const cylinderLength = footprint.barrelLengthM;
  const rimThickness = 0.03;
  const o = footprint.orientation || 'upright';
  const rotation =
    o === 'side-x' ? [0, 0, Math.PI / 2] :
    o === 'side-z' ? [Math.PI / 2, 0, 0] :
    [0, 0, 0];
  return (
    <group rotation={rotation}>
      <mesh castShadow receiveShadow>
        <cylinderGeometry args={[radius * 0.88, radius * 0.88, cylinderLength, 32]} />
        <meshStandardMaterial color={color} roughness={0.7} />
      </mesh>
      <mesh position={[0, -cylinderLength / 2 + rimThickness / 2, 0]} castShadow>
        <cylinderGeometry args={[radius, radius, rimThickness, 32]} />
        <meshStandardMaterial color="#5c4020" roughness={0.9} />
      </mesh>
      <mesh position={[0, cylinderLength / 2 - rimThickness / 2, 0]} castShadow>
        <cylinderGeometry args={[radius, radius, rimThickness, 32]} />
        <meshStandardMaterial color="#5c4020" roughness={0.9} />
      </mesh>
    </group>
  );
}

function BoxMesh({ footprint, color, item }) {
  return (
    <group>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[footprint.lengthM, footprint.heightM, footprint.widthM]} />
        <meshStandardMaterial color={color} roughness={0.6} />
      </mesh>
      {item?.type === 'europallet' && (
        <mesh position={[0, -footprint.heightM / 2 + 0.075, 0]} castShadow>
          <boxGeometry args={[footprint.lengthM, 0.15, footprint.widthM]} />
          <meshStandardMaterial color="#8b5a2b" roughness={0.9} />
        </mesh>
      )}
    </group>
  );
}
